/**
 * Genius Rank universal embed snippet — `gr.js`.
 *
 * One `<script>` tag on any website, doing these jobs:
 *
 *   1. **Install heartbeat** — proves the tag is live, drives the
 *      INSTALLED / STALE / NOT_DETECTED badge in the dashboard.
 *   2. **Structured data** — injects the Organization / LocalBusiness JSON-LD
 *      (with `sameAs` social profiles) built server-side from the site's
 *      business profile, plus any extra `<meta>` tags the config asks for.
 *   3. **Analytics** — a page-view beacon, plus `GeniusRank.track()` for
 *      conversions.
 *   4. **Affiliate attribution** — `?gr_aff=CODE` is persisted for 90 days and
 *      rides along on every event, so a conversion weeks later still credits
 *      the influencer who sent the visitor.
 *   5. **Link-network footer** — for sites in the Genius Rank link network,
 *      appends a small `<nav>` to `<body>` linking the site's references,
 *      blog and sitemap pages — absolute URLs from the API, either the
 *      site's own `/genius/…` (proxy detected) or its hosted copy on
 *      `pages.geniusrank.com`.
 *   6. **Autopilot** — applies the site's approved on-page optimisations
 *      (title, meta description, canonical, h1, image alt, robots, og, JSON-LD,
 *      internal links, image `loading`/`decoding`) for the current path, and
 *      beacons what applied. The image-attribute op (`i`, Phase 43) is applied
 *      for parity and for crawlers that render, but it cannot change how the
 *      current page view already fetched its images — the value that matters
 *      is the server-side one (WordPress plugin, proxy, managed pages). Each op carries the
 *      value the audit observed; when the live page no longer shows it, the
 *      author changed the page since, and the op is skipped.
 *   7. **Reviews widget** — fills a `[data-gr-reviews]` placeholder the site
 *      owner put on the page with a rating badge and recent reviews (plain
 *      text only). The placeholder's children are replaced: placing it is the
 *      owner's opt-in.
 *   8. **Preferred source link** — fills a `[data-gr-preferred-source]`
 *      placeholder with a link to the site's Google Preferred Sources page
 *      (Google's own button needs a third-party script the owner adds
 *      themselves).
 *
 * ## Rules this file lives by
 *
 * This code runs on **somebody else's website**. It is therefore written to be
 * unable to break that website:
 *
 * - Every entry point is wrapped: a thrown error never escapes into the host
 *   page, and total failure degrades to doing nothing at all.
 * - No `document.write` — it would blow away a page that has finished parsing.
 * - Nothing is ever *overwritten*: JSON-LD is skipped if we already added some,
 *   a `<meta>` tag is only added when the page does not already declare it,
 *   and the footer is skipped if ours is already on the page. The customer's
 *   own markup always wins. The one exception is Autopilot, which replaces a
 *   value only on the customer's own approval and only while the page still
 *   shows the value that approval was made against.
 * - Styling is CSSOM property writes only (`el.style.x = …`). A `style`
 *   attribute or an inline `<style>` is refused by a customer CSP without
 *   `style-src 'unsafe-inline'`; property writes are not.
 * - No dependencies, no polyfills, no globals beyond the single
 *   `window.GeniusRank` object. Built to a single IIFE well under 15 KB.
 * - Network calls are fire-and-forget, time-boxed, and use `sendBeacon` where
 *   possible so a beacon never delays a navigation.
 *
 * Constants are duplicated here as literals rather than imported from
 * `@genius-rank/shared`: pulling that package in would drag zod into a bundle
 * that must stay tiny. `src/gr.contract.test.ts` asserts the two agree.
 */

/** Injected by the build (`scripts/build-gr.ts`) from `package.json`. */
declare const __GR_VERSION__: string;
/** Injected by the build — the API base used when the tag does not say. */
declare const __GR_API__: string;

// --- Contract with the API (mirrors @genius-rank/shared) -------------------

const GLOBAL_NAME = 'GeniusRank';
const KEY_ATTRIBUTE = 'data-key';
const API_ATTRIBUTE = 'data-api';
const INJECTED_ATTRIBUTE = 'data-gr';
const CONFIG_PATH = '/snippet/config';
const HEARTBEAT_PATH = '/snippet/heartbeat';
const EVENTS_PATH = '/snippet/events';
const ASSET_PATH = '/snippet/gr.js';
const AFFILIATE_PARAM = 'gr_aff';
const AFFILIATE_STORAGE_KEY = 'gr_aff';
const AFFILIATE_TTL_MS = 90 * 24 * 60 * 60 * 1000;
const EVENT_BATCH_LIMIT = 20;
const FOOTER_MARKER = 'footer';
const FOOTER_SEPARATOR = ' | ';
const OPTIMIZATIONS_PATH = '/snippet/optimizations';
const APPLIED_EVENT = 'gr_applied';
const SKIPPED_EVENT = 'gr_skipped';
const AUTOPILOT_MARKER = 'autopilot';
const REVIEWS_MARKER = 'reviews';
const REVIEWS_HOST = '[data-gr-reviews]';
const REVIEWS_MAX = 5;
const PREFERRED_MARKER = 'preferred-source';
const PREFERRED_HOST = '[data-gr-preferred-source]';
const PREFERRED_TEXT = 'Add us as a preferred source on Google';
/** `preferredSourceUrl` must be a plain `https://www.google.com/…` URL. */
const PREFERRED_URL = /^https:\/\/www\.google\.com\/[\w\-./?=&%]*$/;
const SOURCE_LABELS: Record<string, string> = {
  GOOGLE: 'Google',
  TRUSTPILOT: 'Trustpilot',
  TRIPADVISOR: 'Tripadvisor',
  FIRST_PARTY: 'Collected by us',
};

/**
 * Footer hrefs: a relative `/genius/…` path, or an absolute `https://` URL on a
 * plain hostname (no port, userinfo, query or hash). `..` is refused on top.
 */
const FOOTER_HREF = /^(\/genius\/[\w\-/]+|https:\/\/[\w.-]+(?:\/[\w\-./%]*)?)$/;

/** Config fetches are abandoned after this — a slow API must not linger. */
const CONFIG_TIMEOUT_MS = 5000;

/** Page views are coalesced for this long so one batch carries them all. */
const FLUSH_DELAY_MS = 250;

/** SPA navigations are coalesced for this long before Autopilot re-runs. */
const NAVIGATION_DELAY_MS = 250;

/** A meta `name` / `property` safe to put in an attribute selector. */
const META_KEY = /^[\w:.-]{1,80}$/;

/**
 * `INTERNAL_LINK_BLOCKING_ELEMENTS` / `INTERNAL_LINK_PHRASE_MAX` from
 * `@genius-rank/shared`: no link is created inside these elements' text.
 */
const LINK_BLOCKING =
  'head script style textarea title noscript a h1 h2 h3 h4 h5 h6 button label option select nav header footer';
const LINK_PHRASE_MAX = 120;

interface MetaTag {
  name?: string;
  property?: string;
  content: string;
}

interface FooterLink {
  label: string;
  href: string;
  /** Legacy (pre-hosted-pages API): relative `/genius/…` only. */
  path?: string;
}

/**
 * One Autopilot op, wire form (`WireOp` in `@genius-rank/shared`): kind, new
 * value, observed value (`undefined` = no check, `null` = must be absent or
 * empty) and target (img src for `a`, og property for `o`).
 */
interface Op {
  k: string;
  v: string;
  o?: string | null;
  s?: string;
}

interface Optimizations {
  v?: string;
  /** Normalised path → ops, when the site's map is small enough to inline. */
  inline?: Record<string, Op[]>;
  /** Absolute `…/snippet/optimizations?key=…`; we append `&path=`. */
  url?: string;
}

/** `ReviewWidgetConfig` in `@genius-rank/shared`, minus the schema-only part. */
interface Reviews {
  rating: number | null;
  count: number;
  items?: {
    author: string | null;
    rating: number;
    text: string;
    source: string;
    publishedAt: string;
  }[];
}

interface Settings {
  heartbeat: boolean;
  analytics: boolean;
  jsonLd: boolean;
  heartbeatIntervalSeconds: number;
}

interface Config {
  key: string;
  domain: string;
  snippetVersion: string;
  settings: Settings;
  schemaJson: Record<string, unknown> | null;
  meta: MetaTag[];
  /** Link-network footer links; `null` (or absent) when not a participant. */
  footer?: FooterLink[] | null;
  /** Autopilot map; absent when the site has nothing approved. */
  optimizations?: Optimizations | null;
  /** Reviews widget payload; absent / `null` when there is nothing to show. */
  reviews?: Reviews | null;
  /** Google Preferred Sources link for the domain; absent when unavailable. */
  preferredSourceUrl?: string;
  /** robots.txt fragment for the WordPress plugin — gr.js ignores it. */
  robotsPolicy?: string | null;
}

interface TrackPayload {
  value?: number;
  [key: string]: unknown;
}

interface QueuedEvent {
  type: 'pageview' | 'conversion' | 'custom';
  url?: string;
  referrer?: string;
  affiliateCode?: string;
  value?: number;
  meta?: Record<string, unknown>;
  ts: number;
}

/** The object the host page may pre-declare, and that we finish populating. */
interface GeniusRankGlobal {
  key?: string;
  api?: string;
  version?: string;
  loaded?: boolean;
  /** Calls made before the snippet finished booting: `[type, payload]`. */
  q?: [string, TrackPayload?][];
  track?: (type: string, payload?: TrackPayload) => void;
  affiliateCode?: string | null;
}

const DEFAULT_SETTINGS: Settings = {
  heartbeat: true,
  analytics: true,
  jsonLd: true,
  heartbeatIntervalSeconds: 6 * 60 * 60,
};

// --- Boot ------------------------------------------------------------------

(function main(): void {
  try {
    boot();
  } catch {
    // A snippet that cannot start is a snippet that does nothing. Never a
    // console error on a customer's production site.
  }
})();

function boot(): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  const win = window as unknown as Record<string, unknown>;
  const global = (win[GLOBAL_NAME] as GeniusRankGlobal | undefined) ?? {};
  win[GLOBAL_NAME] = global;

  // Two copies of the tag (a theme plus a plugin, say) must not double every
  // beacon. First one wins.
  if (global.loaded) return;

  const script = locateScript();
  const key = attr(script, KEY_ATTRIBUTE) ?? global.key;
  if (!key) return;

  const api = resolveApiBase(attr(script, API_ATTRIBUTE) ?? global.api, script);

  global.loaded = true;
  global.key = key;
  global.version = __GR_VERSION__;

  const state = createState(key, api);

  // `track()` is published *before* the config request so a page that fires a
  // conversion immediately is not racing the network. Events queue until the
  // config says whether analytics are on.
  global.track = (type: string, payload?: TrackPayload) => {
    try {
      state.track(type, payload);
    } catch {
      /* never throw into the host page */
    }
  };

  global.affiliateCode = state.affiliateCode;

  // Drain anything the page queued via the pre-load stub.
  const pending = Array.isArray(global.q) ? global.q : [];
  global.q = [];
  for (const call of pending) {
    if (Array.isArray(call) && typeof call[0] === 'string') global.track(call[0], call[1]);
  }

  void state.start();
}

// --- State machine ---------------------------------------------------------

function createState(key: string, api: string) {
  const queue: QueuedEvent[] = [];
  const affiliateCode = captureAffiliate();

  let settings: Settings = DEFAULT_SETTINGS;
  let ready = false;
  let flushTimer: ReturnType<typeof setTimeout> | null = null;

  function track(type: string, payload?: TrackPayload): void {
    const normalized: QueuedEvent['type'] =
      type === 'conversion' ? 'conversion' : type === 'pageview' ? 'pageview' : 'custom';

    const meta: Record<string, unknown> = {};
    if (payload) {
      for (const field of Object.keys(payload)) {
        if (field === 'value') continue;
        meta[field] = payload[field];
      }
    }
    // A custom event's real name would otherwise be lost against the three
    // stored types.
    if (normalized === 'custom' && type) meta.event = String(type).slice(0, 120);

    const event: QueuedEvent = {
      type: normalized,
      url: pageUrl(),
      referrer: referrer(),
      ts: Date.now(),
    };

    if (affiliateCode) event.affiliateCode = affiliateCode;
    if (payload && typeof payload.value === 'number' && isFinite(payload.value)) {
      event.value = payload.value;
    }
    if (Object.keys(meta).length > 0) event.meta = meta;

    if (queue.length < EVENT_BATCH_LIMIT * 4) queue.push(event);

    // A conversion is often the last thing that happens before a redirect to a
    // thank-you page, so it does not get to wait for the debounce.
    if (normalized === 'conversion') flush();
    else scheduleFlush();
  }

  function scheduleFlush(): void {
    if (flushTimer !== null) return;
    flushTimer = setTimeout(() => {
      flushTimer = null;
      flush();
    }, FLUSH_DELAY_MS);
  }

  function flush(): void {
    if (!ready || !settings.analytics || queue.length === 0) return;

    const batch = queue.splice(0, EVENT_BATCH_LIMIT);
    post(api + EVENTS_PATH, {
      publicKey: key,
      version: __GR_VERSION__,
      events: batch,
    });

    if (queue.length > 0) scheduleFlush();
  }

  async function start(): Promise<void> {
    const config = await loadConfig(api, key);

    // A config outage must not blind install detection, so the defaults stand
    // in and the heartbeat still goes out. Only the JSON-LD, which we cannot
    // invent client-side, is lost.
    settings = config?.settings ?? DEFAULT_SETTINGS;
    ready = true;

    if (settings.jsonLd && config?.schemaJson) injectJsonLd(config.schemaJson);

    // Autopilot first, so an approved description wins over the add-only
    // meta. A fetched map does not hold up anything but the meta.
    const meta = config?.meta;
    const optimizations = config?.optimizations;
    void autopilot(optimizations, track).then(() => {
      if (meta?.length) injectMeta(meta);
    });
    if (optimizations) onNavigate(() => void autopilot(optimizations, track));
    if (config?.footer?.length) injectFooter(config.footer);
    const reviews = config?.reviews;
    if (reviews) {
      injectReviews(reviews);
      onNavigate(() => injectReviews(reviews, true));
    }
    const preferred = config?.preferredSourceUrl;
    if (typeof preferred === 'string' && PREFERRED_URL.test(preferred)) {
      injectPreferredSource(preferred);
      onNavigate(() => injectPreferredSource(preferred, true));
    }
    if (settings.heartbeat) heartbeat(api, key);

    if (settings.analytics) {
      track('pageview', { ...clientHints() });
      // Anything still queued when the tab goes away goes out on the last
      // beacon opportunity the browser offers.
      onPageHide(flush);
    } else {
      queue.length = 0;
    }
  }

  return { track, start, affiliateCode };
}

// --- Configuration ---------------------------------------------------------

function loadConfig(api: string, key: string): Promise<Config | null> {
  return fetchJson(
    api + CONFIG_PATH + '?key=' + encodeURIComponent(key) + '&url=' + encodeURIComponent(pageUrl()),
  ) as Promise<Config | null>;
}

/** Time-boxed, credential-free GET; `null` on any failure. */
async function fetchJson(url: string): Promise<unknown> {
  try {
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timer = controller ? setTimeout(() => controller.abort(), CONFIG_TIMEOUT_MS) : null;

    const response = await fetch(url, {
      method: 'GET',
      credentials: 'omit',
      mode: 'cors',
      signal: controller ? controller.signal : undefined,
    });

    if (timer !== null) clearTimeout(timer);
    if (!response.ok) return null;

    return (await response.json()) as unknown;
  } catch {
    return null;
  }
}

// --- Injection -------------------------------------------------------------

/**
 * Appends our JSON-LD block, unless we already added one.
 *
 * Only *our* block is checked. A site with hand-written schema keeps it and
 * gets ours alongside: multiple JSON-LD blocks are valid, and search engines
 * merge them by `@id`, which the API sets to `https://domain/#organization`.
 */
function injectJsonLd(schema: Record<string, unknown>): void {
  try {
    const selector = 'script[type="application/ld+json"][' + INJECTED_ATTRIBUTE + '="schema"]';
    if (document.querySelector(selector)) return;

    const node = document.createElement('script');
    node.type = 'application/ld+json';
    node.setAttribute(INJECTED_ATTRIBUTE, 'schema');
    // `textContent` never parses markup, so a `</script>` inside the payload
    // cannot break out of the element.
    node.textContent = JSON.stringify(schema);
    (document.head || document.documentElement).appendChild(node);
  } catch {
    /* ignore */
  }
}

/** Adds meta tags the page is missing. Never touches one it already has. */
function injectMeta(tags: MetaTag[]): void {
  try {
    const head = document.head || document.documentElement;

    for (const tag of tags) {
      const isName = typeof tag.name === 'string' && tag.name.length > 0;
      const attribute = isName ? 'name' : 'property';
      const value = (isName ? tag.name : tag.property) ?? '';

      // The value goes into an attribute selector; anything exotic is dropped
      // rather than escaped, because no legitimate meta name needs it.
      if (!value || !META_KEY.test(value)) continue;
      if (typeof tag.content !== 'string' || !tag.content) continue;
      if (document.querySelector('meta[' + attribute + '="' + value + '"]')) continue;

      const node = document.createElement('meta');
      node.setAttribute(attribute, value);
      node.setAttribute('content', tag.content);
      node.setAttribute(INJECTED_ATTRIBUTE, 'meta');
      head.appendChild(node);
    }
  } catch {
    /* ignore */
  }
}

/**
 * Appends the link-network footer to `<body>`, unless ours is already there.
 *
 * A tag in `<head>` without `defer` runs before `<body>` exists; the config
 * fetch usually outlasts parsing, but if it does not we retry exactly once on
 * `DOMContentLoaded` and otherwise give up. Links are validated client-side
 * too — only `/genius/…` paths or plain `https://host/…` URLs with a non-empty
 * label survive (an entry without `href` falls back to a legacy relative
 * `path`) — and the label goes in via `textContent`, so nothing parses markup.
 */
function injectFooter(links: FooterLink[], retried?: boolean): void {
  try {
    if (document.querySelector('nav[' + INJECTED_ATTRIBUTE + '="' + FOOTER_MARKER + '"]')) return;

    const body = document.body;
    if (!body) {
      if (!retried) {
        document.addEventListener('DOMContentLoaded', () => injectFooter(links, true), {
          once: true,
        });
      }
      return;
    }

    const nav = document.createElement('nav');
    nav.setAttribute(INJECTED_ATTRIBUTE, FOOTER_MARKER);
    nav.setAttribute('aria-label', 'Site links');
    nav.style.textAlign = 'center';
    nav.style.fontSize = '12px';
    nav.style.padding = '16px 8px';
    nav.style.opacity = '.75';

    let count = 0;
    for (const link of links) {
      const label = link?.label;
      let href: unknown = link?.href;
      if (typeof label !== 'string' || !label) continue;
      if (typeof href !== 'string') {
        // Deploy skew: an older API sends `path`, relative only.
        href = link?.path;
        if (typeof href !== 'string' || href[0] !== '/') continue;
      }
      if (!FOOTER_HREF.test(href as string) || (href as string).indexOf('..') >= 0) continue;

      if (count > 0) nav.appendChild(document.createTextNode(FOOTER_SEPARATOR));
      const anchor = document.createElement('a');
      anchor.href = href as string;
      anchor.textContent = label;
      anchor.style.color = 'inherit';
      nav.appendChild(anchor);
      count += 1;
    }

    if (count > 0) body.appendChild(nav);
  } catch {
    /* ignore */
  }
}

// --- Reviews widget --------------------------------------------------------

/**
 * Fills every `selector` placeholder (children replaced) with fresh nodes
 * from `render`. A host whose first child is already ours (`marker`) is left
 * alone, so SPA re-runs only fill new or re-rendered hosts. No host while the
 * document is still parsing: one retry on `DOMContentLoaded`.
 */
function fillHosts(
  selector: string,
  marker: string,
  render: () => Node[],
  retried?: boolean,
): void {
  try {
    const hosts = document.querySelectorAll(selector);
    if (!hosts.length) {
      if (!retried && document.readyState === 'loading') {
        document.addEventListener(
          'DOMContentLoaded',
          () => fillHosts(selector, marker, render, true),
          { once: true },
        );
      }
      return;
    }

    for (let i = 0; i < hosts.length; i += 1) {
      const host = hosts[i] as Element;
      const first = host.children && host.children[0];
      if (first && first.getAttribute(INJECTED_ATTRIBUTE) === marker) continue;
      const nodes = render();
      host.textContent = '';
      for (const node of nodes) host.appendChild(node);
    }
  } catch {
    /* ignore */
  }
}

/**
 * Fills every `[data-gr-reviews]` placeholder with a rating badge and up to
 * five recent reviews. Every string goes in through `textContent`, every
 * element we create carries `data-gr="reviews"`, and styling is CSSOM writes
 * only.
 */
function injectReviews(reviews: Reviews, retried?: boolean): void {
  const count = Math.floor(Number(reviews.count));
  if (count > 0)
    fillHosts(REVIEWS_HOST, REVIEWS_MARKER, () => renderReviews(reviews, count), retried);
}

/**
 * Fills every `[data-gr-preferred-source]` placeholder with one link to the
 * site's Google Preferred Sources page (new tab, `rel="noopener"`), marked
 * `data-gr="preferred-source"`, text through `textContent`, CSSOM styling.
 */
function injectPreferredSource(url: string, retried?: boolean): void {
  fillHosts(
    PREFERRED_HOST,
    PREFERRED_MARKER,
    () => {
      const anchor = document.createElement('a');
      anchor.setAttribute(INJECTED_ATTRIBUTE, PREFERRED_MARKER);
      anchor.href = url;
      anchor.setAttribute('target', '_blank');
      anchor.setAttribute('rel', 'noopener');
      anchor.textContent = PREFERRED_TEXT;
      anchor.style.color = 'inherit';
      return [anchor];
    },
    retried,
  );
}

function renderReviews(reviews: Reviews, count: number): HTMLElement[] {
  const node = (tag: string, text: string, size?: string): HTMLElement => {
    const el = document.createElement(tag);
    el.setAttribute(INJECTED_ATTRIBUTE, REVIEWS_MARKER);
    el.textContent = text;
    if (size) el.style.fontSize = size;
    return el;
  };

  const rating = reviews.rating;
  const badge = node(
    'p',
    (typeof rating === 'number' && isFinite(rating) ? '★ ' + rating.toFixed(1) + ' · ' : '') +
      count +
      (count === 1 ? ' review' : ' reviews'),
    '16px',
  );
  badge.style.margin = '0 0 8px';

  const list = node('div', '');
  let shown = 0;

  const items = Array.isArray(reviews.items) ? reviews.items : [];
  for (const item of items) {
    if (shown >= REVIEWS_MAX) break;
    if (!item || typeof item.text !== 'string') continue;
    const author = item.author;
    if (author !== null && author !== undefined && typeof author !== 'string') continue;
    const stars = item.rating;
    if (typeof stars !== 'number' || !(stars >= 1 && stars <= 5)) continue;
    const full = Math.round(stars);

    const li = node('div', '');
    li.style.margin = '0 0 12px';

    const head = node('p', '', '14px');
    head.style.margin = '0';
    head.appendChild(node('strong', author || 'Anonymous'));
    head.appendChild(document.createTextNode(' '));
    const starsEl = node('span', '★★★★★'.slice(0, full) + '☆☆☆☆☆'.slice(full));
    starsEl.setAttribute('aria-label', full + ' out of 5 stars');
    head.appendChild(starsEl);
    li.appendChild(head);

    const text = node('p', item.text, '14px');
    text.style.margin = '4px 0';
    li.appendChild(text);

    const date = item.publishedAt;
    const dated = typeof date === 'string' && /^\d{4}-\d{2}-\d{2}/.test(date);
    const source = item.source;
    const label =
      typeof source === 'string' && Object.prototype.hasOwnProperty.call(SOURCE_LABELS, source)
        ? SOURCE_LABELS[source]
        : 'Review';
    const meta = node('p', '', '12px');
    meta.appendChild(document.createTextNode(label + (dated ? ' · ' : '')));
    meta.style.margin = '0';
    meta.style.opacity = '.7';
    if (dated) {
      const time = node('time', date.slice(0, 10));
      time.setAttribute('datetime', date);
      meta.appendChild(time);
    }
    li.appendChild(meta);
    list.appendChild(li);
    shown += 1;
  }

  return shown ? [badge, list] : [badge];
}

// --- Autopilot -------------------------------------------------------------

let autopilotPath: string | undefined;

/**
 * Applies the current path's ops and beacons the outcome. Never rejects.
 *
 * The rules are `applyOptimizationsToHtml`'s in `@genius-rank/shared` (and the
 * WordPress plugin's port of it), against the live DOM instead of markup.
 */
async function autopilot(
  map: Optimizations | null | undefined,
  track: (type: string, payload?: TrackPayload) => void,
): Promise<void> {
  try {
    if (!map || typeof map !== 'object') return;
    const path = currentPath();
    if (path === autopilotPath) return;
    autopilotPath = path;

    let ops: unknown = map.inline && map.inline[path];
    const url = map.url;
    if (
      !ops &&
      !map.inline &&
      typeof url === 'string' &&
      url.indexOf(OPTIMIZATIONS_PATH + '?') > 0
    ) {
      ops = (
        (await fetchJson(url + '&path=' + encodeURIComponent(path))) as { ops?: unknown } | null
      )?.ops;
      if (path !== autopilotPath) return;
    }
    if (!Array.isArray(ops)) return;

    const kinds: string[] = [];
    const skipped: string[] = [];
    linked = [];
    for (const op of ops as Op[]) {
      if (!op || typeof op.k !== 'string' || typeof op.v !== 'string') continue;
      let done = false;
      try {
        done = applyOp(op);
      } catch {
        /* counts as skipped */
      }
      (done ? kinds : skipped).push(op.k);
    }
    if (kinds.length + skipped.length) {
      track(kinds.length ? APPLIED_EVENT : SKIPPED_EVENT, { path, kinds, skipped });
    }
  } catch {
    /* ignore */
  }
}

function applyOp(op: Op): boolean {
  const k = op.k;
  if (k === 't') {
    const title = document.querySelector('title');
    if (!observed(title ? title.textContent : null, op)) return false;
    (title || created('title')).textContent = op.v;
    return true;
  }
  if (k === 'm' || k === 'r') {
    return upsert('meta', 'name', k === 'm' ? 'description' : 'robots', 'content', op, k === 'r');
  }
  if (k === 'o') {
    const property = (op.s || '').trim().toLowerCase();
    return META_KEY.test(property) && upsert('meta', 'property', property, 'content', op);
  }
  if (k === 'c') return upsert('link', 'rel', 'canonical', 'href', op);
  if (k === 'h') {
    // Never inserted: a new heading would change the visible layout.
    const h1 = document.querySelector('h1');
    if (!h1 || !observed(h1.textContent, op)) return false;
    h1.textContent = op.v;
    return true;
  }
  if (k === 'a') {
    return images(op, (img) => {
      const alt = img.getAttribute('alt');
      if ((alt && alt.trim()) || !observed(alt, op)) return false;
      img.setAttribute('alt', op.v);
      return true;
    });
  }
  if (k === 'i') {
    // `parseImgAttrsValue`: `key=value;…`, trimmed + lower-cased, allow-listed
    // values, no duplicate key — anything else skips the op. An image that
    // already carries any of the keys (`loading="eager"` included) is left alone.
    const attrs: [string, string][] = [];
    if (!op.s || op.v.length > 200 || !observed(null, op)) return false;
    for (const pair of op.v.split(';')) {
      const at = pair.indexOf('=');
      const name = pair.slice(0, at).trim().toLowerCase();
      const value = pair
        .slice(at + 1)
        .trim()
        .toLowerCase();
      if (at < 0 || !IMG_ATTR.test(name + '=' + value) || attrs.some((a) => a[0] === name)) {
        return false;
      }
      attrs.push([name, value]);
    }
    return images(op, (img) => {
      if (attrs.some((a) => img.getAttribute(a[0]) !== null)) return false;
      for (const a of attrs) img.setAttribute(a[0], a[1]);
      return true;
    });
  }
  if (k === 'j') {
    const selector =
      'script[type="application/ld+json"][' + INJECTED_ATTRIBUTE + '="' + AUTOPILOT_MARKER + '"]';
    if (document.querySelector(selector)) return false;
    const json = JSON.stringify(JSON.parse(op.v));
    const node = created('script');
    node.type = 'application/ld+json';
    node.textContent = json;
    return true;
  }
  if (k === 'l') return link(op);
  if (k === 'x') {
    // `parseHreflangValue`: a JSON array of distinct `{l, h}` (locale code or
    // `x-default`, https href) — one bad entry skips the op. Only langs the
    // page lacks are added; `rel` and `hreflang` are case-insensitive
    // attribute values in an HTML document, so `DE` counts as `de`.
    const add: [string, string][] = [];
    for (const entry of JSON.parse(op.v) as { l: string; h: string }[]) {
      const lang = entry.l.trim().toLowerCase();
      const href = new URL(entry.h).href;
      if (
        !/^(x-default|[a-z]{2,3}(-[a-z0-9]{2,8})*)$/.test(lang) ||
        !/^https:/.test(href) ||
        add.some((pair) => pair[0] === lang)
      ) {
        return false;
      }
      add.push([lang, href]);
    }
    let done = false;
    for (const pair of add) {
      if (!document.querySelector('link[rel~=alternate][hreflang="' + pair[0] + '"]')) {
        const node = created('link');
        node.setAttribute('rel', 'alternate');
        node.setAttribute('hreflang', pair[0]);
        node.setAttribute('href', pair[1]);
        done = true;
      }
    }
    return done;
  }
  return false;
}

/** `IMG_ATTRS_ALLOWED_KEYS` with their safe values, one `name=value` pair. */
const IMG_ATTR =
  /^(loading=(lazy|eager)|decoding=(a?sync|auto)|fetchpriority=(high|low|auto)|(width|height)=[1-9]\d{0,4})$/;

/** Calls `patch` on every `<img>` whose src (raw or resolved) is `op.s`; true if any patched. */
function images(op: Op, patch: (img: HTMLImageElement) => boolean): boolean {
  let hit = false;
  const list = document.querySelectorAll('img[src]');
  for (let index = 0; index < list.length; index += 1) {
    const img = list[index] as HTMLImageElement;
    if ((img.getAttribute('src') === op.s || img.src === op.s) && patch(img)) hit = true;
  }
  return hit;
}

/** Links created by `l` ops earlier in the current run, as `linkId` pairs. */
let linked: string[][] = [];
let wordChar: RegExp | undefined;

/**
 * The `l` op (`INTERNAL_LINK`): wraps the first ASCII-case-insensitive,
 * whole-word occurrence of `s` in a text node outside the blocking elements in
 * `<a href="<v>">`. Skipped when the page (or an earlier op) already links to
 * `v`. The reference's text gaps are this DOM's text nodes.
 */
function link(op: Op): boolean {
  const phrase = (op.s || '').trim();
  const href = op.v.trim();
  const id = linkId(href);
  if (
    !phrase ||
    phrase.length > LINK_PHRASE_MAX ||
    !id ||
    href.length > 2048 ||
    (href[0] === '/' ? href[1] === '/' : !/^https:\/\//i.test(href))
  ) {
    return false;
  }
  const anchors = document.querySelectorAll('a[href]');
  for (let index = 0; index < anchors.length; index += 1) {
    if (sameLink(linkId((anchors[index] as Element).getAttribute('href') || ''), id)) return false;
  }
  for (const other of linked) if (sameLink(other, id)) return false;

  const body = document.body;
  if (!body) return false;
  const walker = document.createTreeWalker(body, 4 /* NodeFilter.SHOW_TEXT */);
  const blocking = LINK_BLOCKING.split(' ');
  for (let node = walker.nextNode() as Text | null; node; node = walker.nextNode() as Text | null) {
    let blocked = false;
    for (
      let up = node.parentNode as Element | null;
      up && up !== body;
      up = up.parentNode as Element | null
    ) {
      if (blocking.indexOf((up.nodeName || '').toLowerCase()) >= 0) blocked = true;
    }
    const hit = blocked ? -1 : findPhrase(node.nodeValue || '', phrase);
    if (hit < 0) continue;
    const middle = node.splitText(hit);
    middle.splitText(phrase.length);
    const anchor = marked('a');
    anchor.setAttribute('href', href);
    (middle.parentNode as Node).replaceChild(anchor, middle);
    anchor.appendChild(middle);
    linked.push(id);
    return true;
  }
  return false;
}

/** First ASCII-case-insensitive, whole-word index of `needle` in `text`, or -1. */
function findPhrase(text: string, needle: string): number {
  const fold = (value: string) => value.replace(/[A-Z]+/g, (run) => run.toLowerCase());
  const haystack = fold(text);
  const target = fold(needle);
  for (let from = 0; from <= haystack.length - target.length;) {
    const hit = haystack.indexOf(target, from);
    if (hit < 0) return -1;
    if (!isWordAt(text, hit - 1) && !isWordAt(text, hit + target.length)) return hit;
    from = hit + 1;
  }
  return -1;
}

/** Is the character at `index` a letter or digit (whole code point)? */
function isWordAt(text: string, index: number): boolean {
  if (index < 0 || index >= text.length) return false;
  // Built lazily: `\p{…}` throws in a browser without it, inside a try.
  wordChar = wordChar || new RegExp('[\\p{L}\\p{N}]', 'u');
  const code = text.charCodeAt(index);
  return wordChar.test(
    code >= 0xd800 && code <= 0xdbff
      ? text.slice(index, index + 2)
      : code >= 0xdc00 && code <= 0xdfff
        ? text.slice(index - 1, index + 1)
        : text.charAt(index),
  );
}

/**
 * `[bare host, normalised path]` of an `http(s)` or relative href (host empty
 * when relative), or null for `mailto:`, `#…` and the like — the reference's
 * `linkIdentity`.
 */
function linkId(href: string): string[] | null {
  const value = href.trim();
  if (!value || value[0] === '#') return null;
  const absolute = value.slice(0, 2) === '//';
  if (!absolute && !/^[a-z][a-z0-9+.-]*:/i.test(value)) return ['', urlPath(value)];
  try {
    const url = new URL(absolute ? 'https:' + value : value);
    if (!/^https?:$/.test(url.protocol)) return null;
    return [
      url.hostname
        .toLowerCase()
        .replace(/\.$/, '')
        .replace(/^www\./, ''),
      urlPath(url.href),
    ];
  } catch {
    return null;
  }
}

function sameLink(a: string[] | null, b: string[]): boolean {
  return !!a && a[1] === b[1] && (!a[0] || !b[0] || a[0] === b[0]);
}

/**
 * Sets `attribute` on `<tag key="value">`, creating the element when absent.
 *
 * Matching is the reference applier's: `key` compared trimmed and
 * case-insensitively (`<meta name="Description">`), and for `rel` as a
 * whitespace-separated token list (`<link rel="canonical alternate">`).
 */
function upsert(
  tag: string,
  key: string,
  value: string,
  attribute: string,
  op: Op,
  caseless?: boolean,
): boolean {
  let node: Element | null = null;
  const candidates = document.querySelectorAll(tag + '[' + key + ']');
  for (let index = 0; index < candidates.length && !node; index += 1) {
    const raw = (candidates[index] as Element).getAttribute(key) || '';
    const folded = raw.trim().toLowerCase();
    if (key === 'rel' ? folded.split(/\s+/).indexOf(value) >= 0 : folded === value) {
      node = candidates[index] as Element;
    }
  }
  if (!observed(node ? node.getAttribute(attribute) : null, op, caseless)) return false;
  if (!node) {
    node = created(tag);
    node.setAttribute(key, value);
  }
  node.setAttribute(attribute, op.v);
  return true;
}

/** A new element of ours in `<head>`, marked so it is recognisable as such. */
function created(tag: string): HTMLScriptElement {
  const node = marked(tag);
  (document.head || document.documentElement).appendChild(node);
  return node;
}

/** A new, detached element marked `data-gr="autopilot"`. */
function marked(tag: string): HTMLScriptElement {
  const node = document.createElement(tag) as HTMLScriptElement;
  node.setAttribute(INJECTED_ATTRIBUTE, AUTOPILOT_MARKER);
  return node;
}

/**
 * The observed-value rule: no `o` = no check; `o === null` = the page must not
 * carry a value; a string = the page must still carry exactly that, after
 * whitespace collapse (robots compared case-insensitively).
 */
function observed(current: string | null | undefined, op: Op, caseless?: boolean): boolean {
  if (op.o === undefined) return true;
  const fold = (text: string) => {
    const collapsed = text.replace(/\s+/g, ' ').trim();
    return caseless ? collapsed.toLowerCase() : collapsed;
  };
  const now = fold(current || '');
  return op.o === null ? !now : !!now && now === fold(String(op.o));
}

/** `normalizeUrlPath` from `@genius-rank/shared`, on the live location. */
function currentPath(): string {
  return normalisePath(window.location.pathname);
}

/** `normalizeUrlPath` from `@genius-rank/shared`, on any href. */
function urlPath(raw: string): string {
  try {
    return normalisePath(new URL(raw.trim(), 'https://autopilot.invalid').pathname);
  } catch {
    return '/';
  }
}

function normalisePath(pathname: string): string {
  let path = pathname.toLowerCase().replace(/\/{2,}/g, '/');
  while (path.length > 1 && path.slice(-1) === '/') path = path.slice(0, -1);
  return (path[0] === '/' ? path : '/' + path).slice(0, 2048);
}

/**
 * Calls `handler` after SPA navigations (`pushState`, `replaceState`, back /
 * forward), debounced. `boot()` runs once per page, so history is wrapped once.
 */
function onNavigate(handler: () => void): void {
  try {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const schedule = () => {
      if (timer !== null) clearTimeout(timer);
      timer = setTimeout(handler, NAVIGATION_DELAY_MS);
    };
    const history = window.history as unknown as Record<string, unknown> | undefined;
    for (const method of ['pushState', 'replaceState']) {
      const original = history && history[method];
      if (typeof original !== 'function') continue;
      (history as Record<string, unknown>)[method] = function (this: unknown, ...args: unknown[]) {
        const result = (original as (...args: unknown[]) => unknown).apply(this, args);
        schedule();
        return result;
      };
    }
    window.addEventListener('popstate', schedule);
  } catch {
    /* ignore */
  }
}

// --- Beacons ---------------------------------------------------------------

/** Once per page load — the badge measures presence, not traffic. */
function heartbeat(api: string, key: string): void {
  post(api + HEARTBEAT_PATH, {
    publicKey: key,
    url: pageUrl(),
    version: __GR_VERSION__,
  });
}

/**
 * Fire-and-forget POST that survives the page being unloaded.
 *
 * `fetch` with `keepalive` is the primary transport, not `sendBeacon`, and the
 * reason is a hard constraint rather than a preference. Our endpoints answer
 * `Access-Control-Allow-Origin: *` — they must, because the calling origin is a
 * customer domain we cannot enumerate. `sendBeacon` always sends with
 * credentials mode `include`, and a browser refuses a wildcard origin in that
 * mode, so a JSON beacon is rejected at the preflight with
 * "the value of the 'Access-Control-Allow-Origin' header must not be the
 * wildcard '*' when the request's credentials mode is 'include'".
 *
 * `keepalive` gives the same unload survival with `credentials: 'omit'`, so the
 * wildcard is legal. `sendBeacon` remains as the fallback for browsers without
 * `keepalive` — with a `text/plain` body, which is a CORS-safelisted content
 * type and therefore travels as a simple no-preflight request. The API parses
 * that content type on the snippet routes for exactly this reason.
 */
function post(url: string, body: unknown): void {
  let payload: string;
  try {
    payload = JSON.stringify(body);
  } catch {
    return;
  }

  try {
    if (supportsKeepalive()) {
      void fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: payload,
        credentials: 'omit',
        mode: 'cors',
        keepalive: true,
      }).catch(noop);
      return;
    }
  } catch {
    /* fall through to the beacon */
  }

  try {
    const beacon = navigator.sendBeacon?.bind(navigator);
    if (beacon) {
      // `text/plain` keeps this a simple request. `application/json` would
      // force a preflight that a wildcard origin cannot satisfy.
      beacon(url, new Blob([payload], { type: 'text/plain;charset=UTF-8' }));
    }
  } catch {
    /* nothing else to try */
  }
}

/** Feature-detects `keepalive`, which older browsers silently ignore. */
function supportsKeepalive(): boolean {
  try {
    if (typeof fetch !== 'function' || typeof Request !== 'function') return false;
    return 'keepalive' in new Request('', { method: 'POST' });
  } catch {
    return false;
  }
}

// --- Affiliate attribution -------------------------------------------------

/**
 * Reads `?gr_aff=CODE` and remembers it for 90 days.
 *
 * Attribution has to outlive the visit — somebody who arrives from an
 * influencer link today and buys next month still credits that influencer.
 * `localStorage` is per-origin, so the code is scoped to the customer's site
 * and never shared across the sites we run on.
 */
function captureAffiliate(): string | null {
  try {
    const fresh = readParam(AFFILIATE_PARAM);
    const store = window.localStorage;

    if (fresh) {
      const code = fresh.slice(0, 120);
      try {
        store?.setItem(
          AFFILIATE_STORAGE_KEY,
          JSON.stringify({ c: code, e: Date.now() + AFFILIATE_TTL_MS }),
        );
      } catch {
        // Private mode / quota. The code still applies to this page view.
      }
      return code;
    }

    const raw = store?.getItem(AFFILIATE_STORAGE_KEY);
    if (!raw) return null;

    const stored = JSON.parse(raw) as { c?: unknown; e?: unknown };
    if (typeof stored.c !== 'string' || typeof stored.e !== 'number') return null;
    if (stored.e < Date.now()) {
      try {
        store?.removeItem(AFFILIATE_STORAGE_KEY);
      } catch {
        /* ignore */
      }
      return null;
    }

    return stored.c;
  } catch {
    return null;
  }
}

// --- Environment helpers ---------------------------------------------------

/**
 * Finds the `<script>` tag that loaded us.
 *
 * `document.currentScript` is correct and cheap, but it is null inside a module
 * or when the file was injected by a tag manager, so the fallback scans for a
 * tag carrying our key.
 */
function locateScript(): HTMLScriptElement | null {
  try {
    const current = document.currentScript as HTMLScriptElement | null;
    if (current && current.getAttribute(KEY_ATTRIBUTE)) return current;

    const tagged = document.querySelector('script[' + KEY_ATTRIBUTE + '^="pk_"]');
    if (tagged) return tagged as HTMLScriptElement;

    const scripts = document.getElementsByTagName('script');
    for (let index = scripts.length - 1; index >= 0; index -= 1) {
      const candidate = scripts[index];
      if (candidate && (candidate.src || '').indexOf('gr.js') !== -1) return candidate;
    }

    return current;
  } catch {
    return null;
  }
}

function attr(element: Element | null, name: string): string | undefined {
  const value = element?.getAttribute(name)?.trim();
  return value ? value : undefined;
}

/**
 * Works out which API to talk to.
 *
 * Explicit `data-api` wins. Otherwise, if we were served from the API itself
 * (`…/api/v1/snippet/gr.js`, which is how local development and the fixtures
 * load it) the base is right there in our own `src`. Production loads from the
 * CDN, where the URL says nothing, and falls back to the build-time default.
 */
function resolveApiBase(explicit: string | undefined, script: HTMLScriptElement | null): string {
  const candidate = explicit ?? deriveApiFromSrc(script) ?? __GR_API__;
  return candidate.replace(/\/+$/, '');
}

function deriveApiFromSrc(script: HTMLScriptElement | null): string | undefined {
  try {
    const src = script?.src;
    if (!src) return undefined;

    const url = new URL(src, window.location.href);
    const path = url.pathname;
    if (path.slice(-ASSET_PATH.length) !== ASSET_PATH) return undefined;

    return url.origin + path.slice(0, path.length - ASSET_PATH.length);
  } catch {
    return undefined;
  }
}

/** Origin + path only. Query strings routinely carry tokens and PII. */
function pageUrl(): string {
  try {
    return (window.location.origin + window.location.pathname).slice(0, 2048);
  } catch {
    return '';
  }
}

function referrer(): string | undefined {
  try {
    const value = document.referrer;
    if (!value) return undefined;
    const url = new URL(value);
    return (url.origin + url.pathname).slice(0, 2048);
  } catch {
    return undefined;
  }
}

function readParam(name: string): string | null {
  try {
    const query = window.location.search;
    if (!query || query.indexOf(name) === -1) return null;

    const match = new RegExp('[?&]' + name + '=([^&#]*)').exec(query);
    if (!match || !match[1]) return null;

    return decodeURIComponent(match[1].replace(/\+/g, ' ')).trim() || null;
  } catch {
    return null;
  }
}

/**
 * Low-entropy client hints for the page-view event.
 *
 * Deliberately not the full user-agent string: the platform hint, language and
 * viewport class answer every question a dashboard asks without adding to the
 * visitor's fingerprint.
 */
function clientHints(): Record<string, unknown> {
  const hints: Record<string, unknown> = {};

  try {
    const uaData = (
      navigator as unknown as { userAgentData?: { platform?: string; mobile?: boolean } }
    ).userAgentData;

    if (uaData?.platform) hints.platform = uaData.platform;
    if (typeof uaData?.mobile === 'boolean') hints.mobile = uaData.mobile;

    if (navigator.language) hints.lang = navigator.language;
    if (window.screen?.width) hints.viewport = window.screen.width + 'x' + window.screen.height;
    if (document.title) hints.title = document.title.slice(0, 200);

    const zone = Intl?.DateTimeFormat?.().resolvedOptions?.().timeZone;
    if (zone) hints.tz = zone;
  } catch {
    /* hints are best-effort */
  }

  return hints;
}

/** The last reliable moment to send a beacon, across browsers. */
function onPageHide(handler: () => void): void {
  try {
    window.addEventListener('pagehide', handler, { capture: false });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') handler();
    });
  } catch {
    /* ignore */
  }
}

function noop(): void {
  /* swallow rejected fetches */
}
